import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useFlexibleMemoizedCallback } from 'pxc-editor-core/helpers/reactHooks';
import {
  mapScaleCoefIntoPercentage,
  getElementByQuerySelector,
  checkCtrlPressed,
} from '@/helpers';
import FloatingButtons from '@/components/FloatingButtons';
import { ZOOM_VALUES, INTERACTIVE_SVG_ID, APP_ENVIRONMENT } from '@/constants';
import { useResetSelection } from '@/hooks';
import FloatableSectionContentTextarea from './FloatableSectionContentTextarea';
import SchemaSvgRenderingAreaContainer from './SchemaSvgRenderingAreaContainer';
import { AreaWrapper } from './styles.js';
import { getAllLayers } from 'pxc-editor-core/helpers/schema';

const scaleFactor = 0.05; // Adjust this value to slow down the movement
let startDistance = 0;
let startX1 = 0,
  startY1 = 0;
let startX2 = 0,
  startY2 = 0;

const SchemasRenderingArea = props => {
  const {
    pages,
    isUserDeviceManipulatorTypeMouse,
    isSectionsSelected,
    scaleCoef,
    targetDevice,
    handleZoomSliderChange,
    setFloatableInputVisible,
    floatableInputVisible,
    handleSectionClick,
  } = props;
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const editorPanelRef = useRef();
  const floatableSectionContentTextareaClbsRef = useRef({
    onFocus: () => {
      setFloatableInputVisible(true);
    },
    onBlur: () => {
      setFloatableInputVisible(false);
    },
  });
  const [value, setValue] = useState(ZOOM_VALUES.DEFAULT_VALUE);
  const [isZoomAvailable, setZoomAvailable] = useState(true);
  const isWebEnvironment = [
    APP_ENVIRONMENT.CLIPX_WEB,
    APP_ENVIRONMENT.WEB_COMPONENT,
  ].includes(process.env.REACT_APP_TARGET);
  const rendererSvgElem = getElementByQuerySelector(`#${INTERACTIVE_SVG_ID}`);
  const isEditModeEnabled = floatableInputVisible;
  const isFloatableTextareaAvailable =
    (isWebEnvironment || isUserDeviceManipulatorTypeMouse) &&
    isSectionsSelected;
  const isSvgRenderingAreaAvailable = !!width && !!height;

  useResetSelection({ isFloatableTextareaAvailable, isEditModeEnabled });

  useEffect(() => {
    if (targetDevice) {
      handleWindowResizeChange();
    }
  }, [targetDevice]);

  useEffect(() => {
    window.addEventListener('resize', handleWindowResizeChange);
    return () => {
      window.removeEventListener('resize', handleWindowResizeChange);
    };
  }, []);

  useEffect(() => {
    if (isWebEnvironment) {
      setValue(mapScaleCoefIntoPercentage(scaleCoef));
    }
  }, [scaleCoef]);

  useEffect(() => {
    if (isWebEnvironment) {
      editorPanelRef.current.addEventListener('mousewheel', handleMousewheel, {
        passive: false,
      });
    }
    return () => {
      if (editorPanelRef.current) {
        editorPanelRef.current.removeEventListener(
          'mousewheel',
          handleMousewheel
        );
      }
    };
  }, [isSectionsSelected, isEditModeEnabled]);

  useEffect(() => {
    if (isWebEnvironment) {
      editorPanelRef.current.addEventListener('touchstart', handleTouchStart);
      editorPanelRef.current.addEventListener('touchmove', handleTouchMove);
      editorPanelRef.current.addEventListener('touchend', handleTouchEnd);
    }
    return () => {
      if (editorPanelRef.current) {
        editorPanelRef.current.removeEventListener(
          'touchstart',
          handleTouchStart
        );
        editorPanelRef.current.removeEventListener(
          'touchmove',
          handleTouchMove
        );
        editorPanelRef.current.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [isSectionsSelected, isEditModeEnabled]);

  const handleWindowResizeChange = () => {
    if (editorPanelRef.current) {
      setWidth(editorPanelRef.current.clientWidth);
      setHeight(editorPanelRef.current.clientHeight);
    }
  };

  const handleMousewheel = event => {
    event.preventDefault();

    if (isEditModeEnabled) {
      setFloatableInputVisible(false);
    }
  };

  const onSvgAfterSectionDblClick = useFlexibleMemoizedCallback(section => {
    if (isFloatableTextareaAvailable) {
      const { shield: parentShield } = getAllLayers(section.id, pages);
      if (!parentShield?.isMatrix) {
        handleSectionClick({ section, floatableInputVisible: true });
      } else {
        setFloatableInputVisible(true);
      }
    }
  });

  const handleWheel = event => {
    if (isWebEnvironment) {
      const isCtrlPressed = checkCtrlPressed(event);

      if (isCtrlPressed) {
        if (event.deltaY > 0) {
          handleZoomSliderChange(
            value - ZOOM_VALUES.STEP >= ZOOM_VALUES.MIN_VALUE
              ? value - ZOOM_VALUES.STEP
              : ZOOM_VALUES.MIN_VALUE
          );
        } else if (event.deltaY < 0) {
          handleZoomSliderChange(
            value + ZOOM_VALUES.STEP <= ZOOM_VALUES.MAX_VALUE
              ? value + ZOOM_VALUES.STEP
              : ZOOM_VALUES.MAX_VALUE
          );
        }
      } else if (rendererSvgElem) {
        const [x, y, width, height] = rendererSvgElem
          .getAttribute('viewBox')
          .split(' ');
        const newX = Number(x) + event.deltaX;
        const newY = Number(y) + event.deltaY;
        const newSvgViewBox = `${newX} ${newY} ${width} ${height}`;

        rendererSvgElem.setAttribute('viewBox', newSvgViewBox);
      }

      if (isEditModeEnabled) {
        setFloatableInputVisible(false);
      }
    }
  };

  const handleTouchStart = event => {
    if (event?.touches?.length > 1) {
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];

      startX1 = touch1.clientX;
      startY1 = touch1.clientY;
      startX2 = touch2.clientX;
      startY2 = touch2.clientY;

      // Calculate initial distance between the two fingers
      startDistance = Math.hypot(startX2 - startX1, startY2 - startY1);
    }
  };

  const handleTouchMove = event => {
    const rendererSvgElem = getElementByQuerySelector(`#${INTERACTIVE_SVG_ID}`);

    if (event?.touches?.length > 1) {
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];

      // Calculate new positions
      const currentX1 = touch1.clientX;
      const currentY1 = touch1.clientY;
      const currentX2 = touch2.clientX;
      const currentY2 = touch2.clientY;

      // Calculate new distance between the two fingers
      const currentDistance = Math.hypot(
        currentX2 - currentX1,
        currentY2 - currentY1
      );

      // Determine if it's a pinch (zoom) or pan (movement)
      if (Math.abs(currentDistance - startDistance) > 10) {
        console.log('Pinch (Zoom) gesture detected');
      } else {
        console.log('Pan (Movement) gesture detected');

        if (rendererSvgElem) {
          setZoomAvailable(false);

          const [x, y, width, height] = rendererSvgElem
            .getAttribute('viewBox')
            .split(' ');
          const touch = event.touches[0];
          const deltaX = (touch.clientX - startX1) * scaleFactor;
          const deltaY = (touch.clientY - startY1) * scaleFactor;
          const newX = Number(x) + deltaX;
          const newY = Number(y) + deltaY;
          const newSvgViewBox = `${newX} ${newY} ${width} ${height}`;

          rendererSvgElem.setAttribute('viewBox', newSvgViewBox);
        }
      }
    }
  };

  const handleTouchEnd = event => {
    setTimeout(() => {
      setZoomAvailable(true);
    }, 0);
  };

  return (
    <AreaWrapper ref={editorPanelRef} onWheel={handleWheel}>
      <FloatingButtons />
      {isFloatableTextareaAvailable && (
        <FloatableSectionContentTextarea
          {...floatableSectionContentTextareaClbsRef.current}
        />
      )}
      {isSvgRenderingAreaAvailable && (
        <SchemaSvgRenderingAreaContainer
          width={width}
          height={height}
          isZoomAvailable={isZoomAvailable}
          onAfterSectionDblClick={onSvgAfterSectionDblClick}
        />
      )}
    </AreaWrapper>
  );
};

export default SchemasRenderingArea;
